import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { AccountingSubNav } from '../components/AccountingSubNav';
import { CheckCircle2, AlertTriangle, Loader2, Landmark, Wallet, FileCheck2 } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { localToday } from '@/shared/utils/utils';

const INPUT = 'rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100';

interface Account { code: string; name: string; _id: string; }
interface Entry { id: string; date: string; narration: string; deposit: number; withdrawal: number; amount: number; cleared: boolean; }
interface ReconData {
  account: { id: string; code: string; name: string };
  entries: Entry[];
  unclearedCount: number;
  clearedCount: number;
  ledgerBalance: number;
  clearedBalance: number;
  statementBalance: number | null;
  difference: number | null;
}

function formatMoney(n: number) {
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Manual tick-to-clear bank reconciliation for Accounting V2's own ledger
 * (AccJournalLine) — see src/api-accounting/banking.js for why this isn't
 * just pointed at the older Accounts module's reconciliation endpoint (that
 * one reconciles a different ledger entirely).
 */
export function BankReconciliationV2Page() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState('');
  const [asOfDate, setAsOfDate] = useState(localToday());
  const [statementBalance, setStatementBalance] = useState('');
  const [data, setData] = useState<ReconData | null>(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkClearing, setBulkClearing] = useState(false);

  useEffect(() => {
    api.get('/api/accounting/banking/accounts')
      .then((res) => {
        const list: Account[] = res.data.data || [];
        setAccounts(list);
        if (list.length > 0) setAccountId(list[0]._id);
      })
      .catch(() => setAccounts([]));
  }, []);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ accountId, asOfDate });
      if (statementBalance !== '') params.set('statementBalance', statementBalance);
      const res = await api.get(`/api/accounting/banking/reconciliation?${params}`);
      setData(res.data.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, asOfDate, statementBalance]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (entry: Entry) => {
    setToggling(entry.id);
    try {
      await api.post('/api/accounting/banking/reconciliation/toggle', { lineId: entry.id, cleared: !entry.cleared });
      await load();
    } catch (err: any) {
      alert(err.response?.data?.error?.message || err.message);
    } finally {
      setToggling(null);
    }
  };

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Bulk-clear the selected entries so a statement import doesn't mean
   * ticking every row one at a time — the toggle endpoint only takes one
   * lineId, so each selected entry is cleared individually and one failure
   * doesn't abort the rest of the batch.
   */
  const bulkClear = async () => {
    if (!selected.size) return;
    setBulkClearing(true);
    const failures: string[] = [];
    for (const id of selected) {
      try {
        await api.post('/api/accounting/banking/reconciliation/toggle', { lineId: id, cleared: true });
      } catch (err: any) {
        failures.push(err.response?.data?.error?.message || err.message);
      }
    }
    setSelected(new Set());
    setBulkClearing(false);
    if (failures.length) alert(`${failures.length} entr${failures.length === 1 ? 'y' : 'ies'} could not be cleared. ${failures[0]}`);
    await load();
  };

  const isReconciled = data?.difference === 0;

  return (
    <div className="">
      <AccountingSubNav />
      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        <PageHeader
          icon={Landmark}
          eyebrow="Finance"
          title="Bank Reconciliation"
          description={<>Tick off every entry that has actually cleared the bank, then prove it against your statement balance</>}
        />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
          className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 flex flex-wrap items-end gap-3"
        >
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Account</label>
            <select className={INPUT} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {accounts.length === 0 && <option value="">No bank/cash accounts found</option>}
              {accounts.map((a) => <option key={a._id} value={a._id}>{a.code} — {a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">As of</label>
            <input type="date" className={INPUT} value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Statement Closing Balance (₹)</label>
            <input type="number" step="0.01" className={INPUT + ' font-mono'} placeholder="Enter to check" value={statementBalance} onChange={(e) => setStatementBalance(e.target.value)} />
          </div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
        ) : !data ? (
          <div className="py-16 text-center text-sm text-slate-400">Select a bank or cash account to begin.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Ledger Balance */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05, ease: [0.4, 0, 0.2, 1] }}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs hover:border-slate-300 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Ledger Balance
                  </span>
                  <div className="mt-1">
                    <span className="text-2xl font-black tracking-tight text-slate-900">
                      {formatMoney(data.ledgerBalance)}
                    </span>
                  </div>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-600 shadow-xs transition-transform group-hover:scale-105">
                  <Wallet className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-slate-500">
                <span>Total Recorded in System</span>
                <span className="font-bold text-blue-700">100%</span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-blue-600 rounded-full" style={{ width: '100%' }} />
              </div>
            </motion.div>

            {/* Cleared Balance */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs hover:border-slate-300 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Cleared Balance
                  </span>
                  <div className="mt-1">
                    <span className="text-2xl font-black tracking-tight text-emerald-600">
                      {formatMoney(data.clearedBalance)}
                    </span>
                  </div>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-emerald-100 bg-emerald-50 text-emerald-600 shadow-xs transition-transform group-hover:scale-105">
                  <FileCheck2 className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-slate-500">
                <span>{data.clearedCount} cleared, {data.unclearedCount} pending</span>
                <span className="font-bold text-emerald-700">
                  {data.clearedCount + data.unclearedCount > 0
                    ? Math.round((data.clearedCount / (data.clearedCount + data.unclearedCount)) * 100)
                    : 0}%
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full bg-emerald-500 rounded-full"
                  style={{
                    width: `${
                      data.clearedCount + data.unclearedCount > 0
                        ? Math.max(5, (data.clearedCount / (data.clearedCount + data.unclearedCount)) * 100)
                        : 0
                    }%`,
                  }}
                />
              </div>
            </motion.div>

            {/* Statement Balance */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.15, ease: [0.4, 0, 0.2, 1] }}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs hover:border-slate-300 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Statement Balance
                  </span>
                  <div className="mt-1">
                    <span className="text-2xl font-black tracking-tight text-slate-900">
                      {data.statementBalance != null ? formatMoney(data.statementBalance) : '—'}
                    </span>
                  </div>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-purple-100 bg-purple-50 text-purple-600 shadow-xs transition-transform group-hover:scale-105">
                  <Landmark className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-slate-500">
                <span>Target Passbook Closing</span>
                <span className="font-bold text-purple-700">Bank Feed</span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-purple-600 rounded-full" style={{ width: '100%' }} />
              </div>
            </motion.div>

            {/* Difference / Recon Status */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className={`group relative overflow-hidden rounded-2xl border p-5 shadow-xs transition-all ${
                data.difference == null
                  ? 'border-slate-200/80 bg-white hover:border-slate-300'
                  : isReconciled
                  ? 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-300'
                  : 'border-rose-200 bg-rose-50/50 hover:border-rose-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    {data.difference == null ? null : isReconciled ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                    )}
                    Difference
                  </span>
                  <div className="mt-1">
                    <span
                      className={`text-2xl font-black tracking-tight ${
                        data.difference == null
                          ? 'text-slate-900'
                          : isReconciled
                          ? 'text-emerald-700'
                          : 'text-rose-700'
                      }`}
                    >
                      {data.difference != null ? formatMoney(data.difference) : '—'}
                    </span>
                  </div>
                </div>
                <div
                  className={`flex h-11 w-11 items-center justify-center rounded-xl border shadow-xs transition-transform group-hover:scale-105 ${
                    data.difference == null
                      ? 'border-slate-200 bg-slate-50 text-slate-600'
                      : isReconciled
                      ? 'border-emerald-200 bg-emerald-100 text-emerald-700'
                      : 'border-rose-200 bg-rose-100 text-rose-700'
                  }`}
                >
                  {isReconciled ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] font-semibold">
                <span className={isReconciled ? 'text-emerald-700 font-bold' : 'text-rose-700 font-bold'}>
                  {data.difference == null
                    ? 'Enter statement balance'
                    : isReconciled
                    ? '✓ Fully Reconciled'
                    : '⚠ Unmatched Variance'}
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${isReconciled ? 'bg-emerald-500' : 'bg-rose-500'}`}
                  style={{ width: isReconciled ? '100%' : '65%' }}
                />
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-surface-3"
          >
            {data.entries.length === 0 ? (
              <div className="py-16 text-center">
                <div className="h-14 w-14 rounded-xl bg-slate-100 dark:from-slate-800 dark:to-slate-900 text-slate-400 flex items-center justify-center mx-auto mb-3">
                  <Landmark className="w-6 h-6" />
                </div>
                <p className="text-sm text-slate-400">No ledger entries for this account yet.</p>
              </div>
            ) : (
              <>
                {(() => {
                  const uncleared = data.entries.filter((e) => !e.cleared).map((e) => e.id);
                  const allSelected = uncleared.length > 0 && uncleared.every((id) => selected.has(id));
                  return uncleared.length > 0 ? (
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-700 px-4 py-2.5 bg-slate-50/60 dark:bg-slate-800/40">
                      <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => setSelected(e.target.checked ? new Set(uncleared) : new Set())}
                          className="h-4 w-4"
                          aria-label="Select all uncleared entries"
                        />
                        {selected.size > 0 ? `${selected.size} selected` : 'Select all uncleared'}
                      </label>
                      {selected.size > 0 && (
                        <div className="flex gap-2">
                          <button
                            onClick={bulkClear}
                            disabled={bulkClearing}
                            className="rounded-lg bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-50"
                          >
                            {bulkClearing ? 'Clearing…' : `Clear ${selected.size}`}
                          </button>
                          <button
                            onClick={() => setSelected(new Set())}
                            className="rounded-lg border border-slate-200 dark:border-slate-700 text-xs font-bold px-3 py-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                          >
                            Clear selection
                          </button>
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                    <tr>{['', 'Cleared', 'Date', 'Narration', 'Deposit', 'Withdrawal'].map((h, i) => (
                      <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {data.entries.map((e) => (
                      <tr key={e.id} className={e.cleared ? 'bg-emerald-50/40 dark:bg-emerald-950/10' : ''}>
                        <td className="px-4 py-2.5">
                          {!e.cleared && (
                            <input
                              type="checkbox"
                              checked={selected.has(e.id)}
                              onChange={() => toggleSelected(e.id)}
                              className="h-4 w-4"
                              aria-label={`Select entry from ${new Date(e.date).toLocaleDateString('en-IN')}`}
                            />
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <input type="checkbox" checked={e.cleared} disabled={toggling === e.id} onChange={() => toggle(e)} className="h-4 w-4" />
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{new Date(e.date).toLocaleDateString('en-IN')}</td>
                        <td className="px-4 py-2.5 text-slate-900 dark:text-slate-100">{e.narration || '—'}</td>
                        <td className="px-4 py-2.5 font-mono text-emerald-600">{e.deposit > 0 ? formatMoney(e.deposit) : ''}</td>
                        <td className="px-4 py-2.5 font-mono text-rose-600">{e.withdrawal > 0 ? formatMoney(e.withdrawal) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </motion.div>
          </>
        )}
      </div>
    </div>
  );
}

export default BankReconciliationV2Page;
